import React, { useEffect, useState } from 'react';
import { Button, Switch, Dropdown, Space } from 'antd';
import Icon, { DiffOutlined, CheckSquareOutlined, PlusOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  buildFilterSelector,
  compareDrawerTitleSelector,
  compareFiltersSelector,
  compareTypeSelector,
} from '@store/trends/base/selectors';
import { FilterBarFooter, StyledDrawer } from './styled';
import FilterBar from './FilterBar';
import {
  compareFilters,
  compareFiltersOnTrends,
  initCompare,
  removeCompareRule,
  resetCompare,
  updateCompareRule,
} from '@store/trends/base/actions';
import CompareBar from './CompareBar';
import FilterBarAdvanced from './FilterBarAdvanced';
import { useHistory } from 'react-router-dom';
import { tid } from '@utils/test';
import PrimaryFilterTag from './PrimaryFilterTag';
import { COMPARE_TYPES } from '@store/trends/base/reducer';
import CompareIco from '@icons/charts/compare.svg';
import { scrollToCompareFiltersBar } from '@components/summary-panel/CompareFiltersBar/utils';
import apptracker from '@utils/sensor/apptracker';
import { APPLY_COMPARE } from '@src/utils/sensor/track';

const HelpText = () => (
  <div>
    <p>
      {' '}
      Quick add top values using <Icon component={CompareIco} /> .
    </p>
    <p>
      {' '}
      Add filters using <PlusOutlined /> icon and use <CheckSquareOutlined /> to add more values to
      the selected filter.
    </p>
  </div>
);

function CompareBuilder({ onToggle, visible, disabled }) {
  const dispatch = useDispatch();
  const filters = useSelector(compareFiltersSelector);
  const [advanced, setAdvanced] = useState(true);
  const history = useHistory();
  const title = useSelector(compareDrawerTitleSelector);

  const generatedFilter = useSelector(buildFilterSelector);
  const { rule = [] } = generatedFilter;
  const type = useSelector(compareTypeSelector);

  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (filters.length > 0) {
      onToggle(true);
    }
  }, [filters.length]);

  // compare menu
  const handleClick = () => {
    onToggle(true);
    dispatch(initCompare(COMPARE_TYPES.trends));
  };

  const handleOk = () => {
    setApplying(true);
    // to render button state change
    setTimeout(() => {
      if (type === COMPARE_TYPES.trends) {
        dispatch(compareFiltersOnTrends());
      } else {
        dispatch(compareFilters({ history }));
      }
      handleCancel();
      scrollToCompareFiltersBar();
      apptracker(APPLY_COMPARE, { type });
    }, 50);
  };

  const handleCancel = () => {
    onToggle(false);
    // to accomodate drawer close animation
    setTimeout(() => {
      dispatch(resetCompare());
      setApplying(false);
    }, 50);
  };

  const onSwitchChange = (checked: boolean) => {
    setAdvanced(checked);
  };

  return (
    <>
      <Button onClick={handleClick} disabled={disabled} icon={<DiffOutlined />}>
        Compare
      </Button>

      <StyledDrawer
        title={
          <Space style={{ alignItems: 'baseline' }}>
            <h3>{title}</h3>
            <Switch
              checkedChildren="Advanced"
              unCheckedChildren="Basic"
              onChange={onSwitchChange}
              defaultChecked
              {...tid('compare-drawer-switch')}
            />
          </Space>
        }
        placement="top"
        onClose={handleCancel}
        open={visible}
        key="compare-builder"
        mask={false}
        height={310}
        zIndex={999}
        footer={
          <FilterBarFooter {...tid('compare-drawer-footer')}>
            <Button type="primary" onClick={handleOk} disabled={applying || filters.length <= 0}>
              {applying ? 'Applying...' : 'Apply'}
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
          </FilterBarFooter>
        }
      >
        <div {...tid('compare-drawer-body')}>
          {type === COMPARE_TYPES.trends && <PrimaryFilterTag />}
          <CompareBar filters={filters} type={type} />
          <div>
            {rule.length === 0 ? (
              <HelpText />
            ) : advanced ? (
              <FilterBarAdvanced
                rules={rule}
                removeRule={removeCompareRule}
                updateRule={updateCompareRule}
              />
            ) : (
              <FilterBar rules={rule} removeRule={removeCompareRule} />
            )}
          </div>
        </div>
      </StyledDrawer>
    </>
  );
}

export default CompareBuilder;
